import { HttpContextToken, HttpInterceptorFn, HttpStatusCode } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { adaptApiError, ToastService, ToastType } from '@pin-up-global/common';
import { FlowEditorError as FlowEditorErrorData } from '@pin-up-global/flow-editor';
import { calculateAverageReadingTime } from '@shared/utils';
import { ApiErrorCode } from '@core/api';
import { isInterceptorDisabled } from './is-interceptor-disabled';
import { ApiError, ErrorObject } from './api-errors.interceptor.abstraction';

export const DISABLE_API_ERRORS_INTERCEPTOR = new HttpContextToken(() => false);

export const apiErrorsInterceptor: HttpInterceptorFn = (request, next) => {
  const toastService = inject(ToastService);

  if (isInterceptorDisabled(request.context, DISABLE_API_ERRORS_INTERCEPTOR)) {
    return next(request);
  }

  return next(request).pipe(
      adaptApiError(),
      catchError((error: ApiError) => {
        if (error.originalError!.status === HttpStatusCode.Unauthorized || error.code === ApiErrorCode.TokenNotValid) {
          return throwError(() => error);
        }

        const formMessage = (errorData: typeof error.data, parentKey?: string): string => {
          if (!errorData) {
            return '';
          }

          if (error.code === ApiErrorCode.FlowDataInvalid) {
            return (<FlowEditorErrorData>errorData).map((error) => error.message).join('\n');
          }

          if (typeof errorData === 'string') {
            return errorData;
          }

          return Object.entries(errorData).map(([key, value]) => {
            const fullKey = parentKey ? `${ parentKey }.${ key }` : key;

            if (typeof value === 'string') {
              return `${ fullKey }: ${ value }`;
            } else if (Array.isArray(value) && value.length && typeof value[0] === 'string') {
              return `${ fullKey }: ${ value.join('; ') }`;
            } else {
              return formMessage(<ErrorObject>value, fullKey);
            }
          }).join('\n');
        };
        const message = formMessage(error.data);
        const averageReadingTime = calculateAverageReadingTime(message);
        const timeout = Math.max(averageReadingTime, 5000);

        toastService.show({
          type: ToastType.Error,
          title: error.message,
          message,
          timeout,
        });

        return throwError(() => error);
      }),
  );
};
